import { Box, Container, Divider, Link, Paper, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import Footer from "../components/common/Footer";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      {children}
    </Box>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: "#0F1A1A",
        color: "#E6F1F0",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: "0.8125rem",
        lineHeight: 1.6,
        overflowX: "auto",
        whiteSpace: "pre",
        borderRadius: 1.5,
      }}
    >
      {children}
    </Paper>
  );
}

export default function SkillPage() {
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ flex: 1, py: 6 }}>
        <Link component={RouterLink} to="/" variant="body2" color="text.secondary" underline="hover" sx={{ display: "inline-block", mb: 4 }}>
          ← Back to īmēra
        </Link>

        <Typography variant="h4" gutterBottom>Skill — sending email through īmēra</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
          īmēra ships with a Claude Code skill (<code>imera-send</code>) that teaches an agent how to add transactional email to any of your apps — signup confirmations, password resets, magic links, receipts, broadcast announcements. The same instructions are below for humans.
        </Typography>

        <Divider sx={{ mb: 4 }} />

        <Section title="1. Get an API key">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mb: 2 }}>
            Each consuming app gets its own key.
          </Typography>
          <Box component="ol" sx={{ pl: 2.5, "& li": { mb: 0.75 } }}>
            <Typography component="li" variant="body2" color="text.secondary">Sign in to the dashboard.</Typography>
            <Typography component="li" variant="body2" color="text.secondary"><strong>Apps</strong> → <strong>New app</strong> → fill name + owner email.</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Click into the app → <strong>Issue API key</strong> → copy the plaintext key (shown once).</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Store it as <code>IMERA_API_KEY</code> in the consuming app's secrets manager. Never commit it.</Typography>
          </Box>
        </Section>

        <Section title="2. Install the client">
          <Code>{`npm install @imera/client`}</Code>
        </Section>

        <Section title="3. Send">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mb: 1.5 }}>
            With a template:
          </Typography>
          <Code>{`import { createImeraClient } from "@imera/client";

const imera = createImeraClient({ apiKey: process.env.IMERA_API_KEY! });

await imera.send({
  to: "user@example.com",
  from: "hello@techlead.co.nz",
  templateId: "welcome",
  variables: { name: "Liam" },
});`}</Code>

          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mt: 3, mb: 1.5 }}>
            Without a template:
          </Typography>
          <Code>{`await imera.send({
  to: "user@example.com",
  from: "hello@techlead.co.nz",
  subject: "Your code",
  html: "<p>Code: <strong>483921</strong></p>",
});`}</Code>

          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mt: 3, mb: 1.5 }}>
            Or call the HTTP API directly from any language:
          </Typography>
          <Code>{`curl -X POST https://imera.kotahirau.com/api/send \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: $IMERA_API_KEY" \\
  -d '{"to":"user@example.com","from":"hello@techlead.co.nz","templateId":"welcome","variables":{"name":"Liam"}}'`}</Code>
        </Section>

        <Section title="Verified from-domains">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            The <code>from</code> address must use one of the registered domains, otherwise the request returns <code>400 unknown_from_domain</code>. Add new domains via <strong>Providers</strong> → <strong>Add domain</strong> after verifying the domain in Brevo or Resend.
          </Typography>
        </Section>

        <Section title="Important constraints">
          <Box component="ul" sx={{ pl: 2.5, "& li": { mb: 0.75 } }}>
            <Typography component="li" variant="body2" color="text.secondary">
              <strong>Server-side only.</strong> Never put <code>IMERA_API_KEY</code> in browser or mobile clients — keys are not scoped per request.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              <strong>Sync send.</strong> The call returns success once the provider accepts the message. Delivery / bounce / open events arrive later via webhook and are visible on the Audit page.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              <strong>One key per app.</strong> So the audit log attributes activity correctly and you can revoke individually.
            </Typography>
          </Box>
        </Section>

        <Section title="Error codes">
          <Box component="ul" sx={{ pl: 2.5, "& li": { mb: 0.5 } }}>
            <Typography component="li" variant="body2" color="text.secondary"><code>unauthorized</code> — missing, invalid, or revoked API key.</Typography>
            <Typography component="li" variant="body2" color="text.secondary"><code>bad_request</code> — schema validation failed.</Typography>
            <Typography component="li" variant="body2" color="text.secondary"><code>unknown_from_domain</code> — the <code>from</code> domain has no provider config.</Typography>
            <Typography component="li" variant="body2" color="text.secondary"><code>template_not_found</code> — the <code>templateId</code> doesn't exist.</Typography>
            <Typography component="li" variant="body2" color="text.secondary"><code>template_render_failed</code> — variables missing or wrong type.</Typography>
            <Typography component="li" variant="body2" color="text.secondary"><code>provider_failed</code> — both primary and fallback (if set) failed.</Typography>
          </Box>
        </Section>

        <Section title="Use it from Claude Code">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            Install the skill source from the <code>imera-email</code> repo to your global skills directory, then ask Claude Code to "add email sending via īmēra" in any project. The agent will pick up the skill and walk through the steps above automatically.
          </Typography>
        </Section>
      </Container>

      <Footer />
    </Box>
  );
}
